import { getApiUrl } from './config';
import type { EnvironmentFamily, EnvironmentResult, EnvironmentSource, EnvironmentState, EnvironmentSubject } from './parcel-environment-types';

export class ParcelEnvironmentClientError extends Error {
  constructor(message: string, public readonly status: number | null = null) { super(message); }
}
export interface EnvironmentView { state: EnvironmentState | null; busy: boolean; error: ParcelEnvironmentClientError | null }
const hash = /^sha256:[a-f0-9]{64}$/;
const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
const statuses = new Set(['idle', 'queued', 'running', 'ready', 'unavailable', 'failed', 'expired', 'interrupted', 'superseded']);
const text = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;
const nullableText = (value: unknown) => value === null || text(value);
const date = (value: unknown) => value === null || (text(value) && Number.isFinite(Date.parse(value)));
function validSource(source: EnvironmentSource | null | undefined): source is EnvironmentSource {
  return !!source && hash.test(source.source_id) && text(source.label) && date(source.source_date) && text(source.currentness);
}
function sameSubject(a: EnvironmentSubject | undefined, b: EnvironmentSubject) {
  return !!a && a.parcel_id === b.parcel_id && !!b.boundary_revision && a.boundary_revision === b.boundary_revision;
}

// Used at both transport and rendering boundaries; a malformed family never becomes a partial numeric display.
export function isEnvironmentResult(value: EnvironmentResult | null | undefined, subject: EnvironmentSubject, family: EnvironmentFamily): value is EnvironmentResult {
  if (!value || value.family !== family || !sameSubject(value.subject, subject) || !hash.test(value.result_id) ||
    !validSource(value.source) || !text(value.method_version) || !text(value.computed_at) || !date(value.computed_at) || !value.value) return false;
  if (value.family === 'epa') {
    const pollutants = (rows: unknown) => Array.isArray(rows) && rows.every(row => row && text(row.pollutant) && text(row.standard));
    return typeof value.value.intersects_nonattainment === 'boolean' && pollutants(value.value.pollutants) && pollutants(value.value.maintenance);
  }
  const metric = value.value;
  return Array.isArray(metric.zones) && metric.zones.every(text) &&
    typeof metric.overlap_acres_100yr === 'number' && Number.isFinite(metric.overlap_acres_100yr) && metric.overlap_acres_100yr >= 0 &&
    typeof metric.overlap_pct_100yr === 'number' && Number.isFinite(metric.overlap_pct_100yr) && metric.overlap_pct_100yr >= 0 && metric.overlap_pct_100yr <= 100 && metric.coverage_pct === 100;
}

function validate(value: unknown, subject: EnvironmentSubject, family: EnvironmentFamily): EnvironmentState {
  const state = value as EnvironmentState;
  const invalid = () => new ParcelEnvironmentClientError('Environmental evidence did not match this parcel, family and source. Check saved status.');
  if (!state || state.family !== family || state.subject?.parcel_id !== subject.parcel_id) throw invalid();
  if (!sameSubject(state.subject, subject)) throw new ParcelEnvironmentClientError('The boundary changed. Reload the parcel to check environmental evidence.', 409);
  if (!statuses.has(state.status) || typeof state.can_calculate !== 'boolean' || typeof state.can_retry !== 'boolean' ||
    !Number.isInteger(state.attempt) || state.attempt < 0 || !nullableText(state.reason) ||
    (state.identity_key !== null && !hash.test(state.identity_key)) || (state.request_id !== null && !uuid.test(state.request_id)) ||
    !date(state.queued_at) || !date(state.started_at) || !date(state.deadline_at) || !date(state.retry_after) ||
    (state.source !== null && !validSource(state.source))) throw invalid();
  if ((state.can_calculate || state.can_retry || ['queued', 'running', 'ready'].includes(state.status)) && (!state.identity_key || !validSource(state.source))) throw invalid();
  if (['queued', 'running', 'ready'].includes(state.status) && !state.request_id) throw invalid();
  if (['queued', 'running'].includes(state.status) && !state.deadline_at) throw invalid();
  if (state.status === 'ready') {
    if (!isEnvironmentResult(state.result, subject, family) || state.result.result_id !== state.identity_key ||
      state.result.source.source_id !== state.source?.source_id) throw invalid();
  } else if (state.result !== null) throw invalid();
  if (state.previous_result !== null && !isEnvironmentResult(state.previous_result, subject, family)) throw invalid();
  return state;
}

function headers() {
  const token = globalThis.localStorage?.getItem('pp-auth');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export function createParcelEnvironmentClient(fetchImpl: typeof fetch = globalThis.fetch) {
  async function request(url: string, init: RequestInit, controller: AbortController, release = false): Promise<unknown> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let aborted: (() => void) | undefined;
    const timeout = new Promise<never>((_, reject) => {
      aborted = () => reject(new ParcelEnvironmentClientError('The environmental request was stopped.'));
      controller.signal.addEventListener('abort', aborted, { once: true });
      timer = setTimeout(() => {
        reject(new ParcelEnvironmentClientError('The environmental request reached its time limit. Check saved status before trying again.'));
        controller.abort();
      }, 8000);
    });
    try {
      return await Promise.race([timeout, (async () => {
        const response = await fetchImpl(url, { ...init, headers: headers(), cache: 'no-store', signal: controller.signal });
        if (!response.ok) {
          throw new ParcelEnvironmentClientError(response.status === 409 ? 'The boundary or source changed. Reload the parcel before checking again.' :
            response.status === 401 || response.status === 403 ? 'Environmental calculations require the owner session.' :
              'Environmental status could not be confirmed. Check saved status before trying again.', response.status);
        }
        return release ? null : await response.json();
      })()]);
    } finally {
      clearTimeout(timer);
      if (aborted) controller.signal.removeEventListener('abort', aborted);
    }
  }

  return { watch(subject: EnvironmentSubject, family: EnvironmentFamily, publish: (view: EnvironmentView) => void) {
    const url = `${getApiUrl()}/parcel-panel/${encodeURIComponent(subject.parcel_id)}/environment/${family}`;
    const subscribers = new Set<string>();
    const automatic = new Set<string>();
    let disposed = false, generation = 0;
    let view: EnvironmentView = { state: null, busy: false, error: null };
    let active: AbortController | undefined;
    let polling: ReturnType<typeof setTimeout> | undefined;
    let deadline: ReturnType<typeof setTimeout> | undefined;
    let cycleEnd = 0;
    const current = (token: number) => !disposed && generation === token;
    function emit(next: EnvironmentView) { if (!disposed) { view = next; publish(next); } }
    function clearTimers() { clearTimeout(polling); clearTimeout(deadline); }
    function failed(error: unknown, token: number) {
      if (!current(token)) return;
      clearTimers(); active?.abort(); generation++;
      emit({ state: view.state, busy: false, error: error instanceof ParcelEnvironmentClientError ? error :
        new ParcelEnvironmentClientError('Environmental status could not be confirmed. Check saved status before trying again.') });
    }
    async function receive(method: 'GET' | 'POST', token: number, body?: unknown) {
      active = new AbortController();
      const response = await request(url, { method, ...(body ? { body: JSON.stringify(body) } : {}) }, active);
      if (!current(token)) return null;
      return validate(response, subject, family);
    }
    function accept(state: EnvironmentState, token: number, expected?: EnvironmentState) {
      if (!current(token)) return;
      if (expected && (state.identity_key !== expected.identity_key || state.request_id !== expected.request_id)) {
        throw new ParcelEnvironmentClientError('The environmental request or source version changed. Check saved status to load its current identity.');
      }
      clearTimers();
      const running = state.status === 'queued' || state.status === 'running';
      if (running) {
        const remaining = Math.min(cycleEnd, Date.parse(state.deadline_at!)) - Date.now();
        if (remaining <= 0) throw new ParcelEnvironmentClientError('The environmental waiting time limit ended. Check saved status for its outcome.');
        deadline = setTimeout(() => failed(new ParcelEnvironmentClientError('The environmental waiting time limit ended. Check saved status for its outcome.'), token), remaining);
        polling = setTimeout(() => {
          if (!current(token)) return;
          void receive('GET', token).then(next => { if (next) accept(next, token, state); }).catch(error => failed(error, token));
        }, Math.min(1000, remaining));
      }
      emit({ state, busy: running, error: null });
    }
    function submit(state: EnvironmentState, retry: boolean) {
      if (disposed || !state.identity_key || !subject.boundary_revision) return;
      clearTimers(); active?.abort(); const token = ++generation;
      cycleEnd = Date.now() + 45000;
      automatic.add(state.identity_key);
      // Retain the ID before transport: an uncertain POST can still have registered its interest.
      const subscriber_id = globalThis.crypto.randomUUID();
      subscribers.add(subscriber_id);
      emit({ state, busy: true, error: null });
      const body = { expected_boundary_revision: subject.boundary_revision, expected_identity_key: state.identity_key, subscriber_id, ...(retry ? { retry: true } : {}) };
      void receive('POST', token, body).then(next => {
        if (!next) return;
        if (next.identity_key !== state.identity_key) throw new ParcelEnvironmentClientError('The environmental input changed. Reload the parcel before checking again.', 409);
        accept(next, token, !retry && (state.status === 'queued' || state.status === 'running') ? state : undefined);
      }).catch(error => failed(error, token));
    }
    function load(auto: boolean) {
      if (disposed || view.busy) return;
      clearTimers(); active?.abort(); const token = ++generation;
      cycleEnd = Date.now() + 45000;
      emit({ ...view, busy: true, error: null });
      void receive('GET', token).then(state => {
        if (!state) return;
        const join = state.status === 'queued' || state.status === 'running';
        const eligible = state.can_calculate && (state.status === 'idle' || (state.status === 'superseded' && state.reason === 'ENVIRONMENT_ABANDONED'));
        if (state.identity_key && !automatic.has(state.identity_key) && (join || (auto && eligible))) submit(state, false);
        else accept(state, token);
      }).catch(error => failed(error, token));
    }
    if (subject.boundary_revision && hash.test(subject.boundary_revision) && uuid.test(subject.parcel_id)) load(true);
    else emit({ state: null, busy: false, error: new ParcelEnvironmentClientError('A verified selected parcel boundary is required.', 409) });
    return {
      dispose() {
        if (disposed) return;
        disposed = true; generation++; clearTimers(); active?.abort();
        for (const subscriber_id of subscribers) {
          void request(`${getApiUrl()}/parcel-panel/environment/release`, { method: 'POST', body: JSON.stringify({ subscriber_id }), keepalive: true }, new AbortController(), true).catch(() => {});
        }
        subscribers.clear();
      },
      check() { load(false); },
      run() {
        const state = view.state;
        if (!view.busy && state?.can_calculate && (state.status === 'idle' || (state.status === 'superseded' && state.reason === 'ENVIRONMENT_ABANDONED'))) submit(state, false);
      },
      retry() {
        const state = view.state;
        if (!view.busy && state?.can_retry && ['failed', 'expired', 'interrupted'].includes(state.status) && (!state.retry_after || Date.parse(state.retry_after) <= Date.now())) submit(state, true);
      },
    };
  } };
}
export const parcelEnvironmentClient = createParcelEnvironmentClient();
